import { useRef, useState } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import GridOnIcon from '@mui/icons-material/GridOn';
import HeightIcon from '@mui/icons-material/Height';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PushPinIcon from '@mui/icons-material/PushPin';
import TuneIcon from '@mui/icons-material/Tune';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import {
  Box,
  Button,
  Checkbox,
  ClickAwayListener,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grow,
  IconButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Tooltip,
  Typography,
} from '@mui/material';
import { useZoom } from 'context/ZoomContext';
import { ClearAttributeMappingsPayload } from 'services/sheet-attribute-manager/models/sheet-attribute-manager.interface';
import { MappingActionsToolbarProps } from './MappingActionsToolbar.props';
import {
  getClearMappingsButtonSx,
  getCreateLabelsButtonSx,
  getCreateLabelsTextSx,
  getMenuItemSx,
  getMenuListSx,
  getMenuPaperSx,
  getNestedMenuItemSx,
  getPushMappingsButtonSx,
  getToolbarContainerSx,
  getViewerOptionsGroupSx,
  getViewerOptionsIconSx,
  popperSx,
} from './MappingActionsToolbar.styles';
import { XLSX_VIEWER_STRINGS } from './xlsx-viewer.contants';

const MappingActionsToolbar = ({
  documentReferenceId,
  activeSheetLabel,
  availableFeatureOptions,
  leadingActions,
  onPreviewFeatureOptionToggle,
  onPushMappings,
  onClearMappings,
  onReprocessDocument,
  previewFeatureOptions,
  primaryReprocessLabel,
  showCreateLabels = true,
  showPushMappings = false,
}: MappingActionsToolbarProps) => {
  const { zoomLevel } = useZoom();
  const [open, setOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [viewerOptionsOpen, setViewerOptionsOpen] = useState(false);
  const [pendingClearPayload, setPendingClearPayload] = useState<ClearAttributeMappingsPayload>();
  const anchorRef = useRef<HTMLButtonElement>(null);

  const handleToggle = () => {
    setOpen(prevOpen => !prevOpen);
  };

  const handleClose = (event: Event | React.SyntheticEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return;
    }
    setOpen(false);
    setViewerOptionsOpen(false);
  };

  const handleOpenConfirmDialog = (payload?: ClearAttributeMappingsPayload) => {
    setPendingClearPayload(payload);
    setConfirmDialogOpen(true);
    setOpen(false);
  };

  const handleCloseConfirmDialog = () => {
    setConfirmDialogOpen(false);
    setPendingClearPayload(undefined);
  };

  const handleConfirmClearMappings = () => {
    onClearMappings(pendingClearPayload);
    handleCloseConfirmDialog();
  };

  const handleCreateLabels = () => {
    onReprocessDocument?.();
    setOpen(false);
  };

  const handleTogglePreviewFeatureOption = (key: keyof NonNullable<typeof previewFeatureOptions>) => {
    onPreviewFeatureOptionToggle?.(key);
  };

  const handleToggleViewerOptions = () => {
    setViewerOptionsOpen(prevOpen => !prevOpen);
  };

  const hasActiveSheetLabel = Boolean(activeSheetLabel);
  const previewFeatureMenuItems = previewFeatureOptions && availableFeatureOptions ? [
    { key: 'zoom', label: 'Zoom controls', Icon: ZoomInIcon },
    { key: 'columnResize', label: 'Resize columns', Icon: ViewColumnIcon },
    { key: 'rowResize', label: 'Resize rows', Icon: HeightIcon },
    { key: 'stickyReferenceColumn', label: 'Sticky column', Icon: PushPinIcon },
    { key: 'stickyReferenceRow', label: 'Sticky row', Icon: PushPinIcon },
    { key: 'showGridLegends', label: 'Show legends', Icon: GridOnIcon },
  ] as const : [];
  const visiblePreviewFeatureMenuItems = previewFeatureMenuItems.filter(item => availableFeatureOptions?.[item.key]);

  return (
    <>
      <Box sx={getToolbarContainerSx(zoomLevel)}>
        {leadingActions}

        {showCreateLabels && (
          <Button variant="text" size="small" onClick={handleCreateLabels} sx={getCreateLabelsButtonSx(zoomLevel)}>
            <Typography component="span" sx={getCreateLabelsTextSx(zoomLevel)}>
              {primaryReprocessLabel ?? XLSX_VIEWER_STRINGS.BUTTON.CREATE_LABELS}
            </Typography>
          </Button>
        )}

        {showPushMappings && (
          <Tooltip title="Push Mappings">
            <Button
              variant="contained"
              color="success"
              size="small"
              onClick={onPushMappings}
              sx={getPushMappingsButtonSx(zoomLevel)}>
              {XLSX_VIEWER_STRINGS.BUTTON.PUSH_MAPPINGS}
            </Button>
          </Tooltip>
        )}

        <Tooltip title={XLSX_VIEWER_STRINGS.MENU.CLEAR_MENU_TOOLTIP}>
          <IconButton
            ref={anchorRef}
            size="small"
            onClick={handleToggle}
            aria-controls={open ? XLSX_VIEWER_STRINGS.ARIA.MENU_ID : undefined}
            aria-expanded={open ? 'true' : undefined}
            aria-haspopup="menu"
            sx={getClearMappingsButtonSx(zoomLevel)}>
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Popper
        sx={popperSx}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        placement="bottom-end">
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
            }}>
            <Paper sx={getMenuPaperSx(zoomLevel)}>
              <ClickAwayListener onClickAway={handleClose}>
                <Box>
                  <MenuList autoFocusItem id={XLSX_VIEWER_STRINGS.ARIA.MENU_ID} sx={getMenuListSx(zoomLevel)}>
                    {visiblePreviewFeatureMenuItems.length ? (
                      <>
                        <MenuItem
                          aria-expanded={viewerOptionsOpen ? 'true' : undefined}
                          sx={getMenuItemSx(zoomLevel)}
                          onClick={handleToggleViewerOptions}>
                          <ListItemIcon sx={getViewerOptionsIconSx(zoomLevel)}>
                            <TuneIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText primary={XLSX_VIEWER_STRINGS.MENU.VIEWER_OPTIONS} />
                          <ExpandMoreIcon
                            fontSize="small"
                            sx={{
                              transform: viewerOptionsOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                              transition: 'transform 140ms ease',
                            }}
                          />
                        </MenuItem>
                        <Box component="li" sx={{ listStyle: 'none' }}>
                          <Collapse in={viewerOptionsOpen} timeout={160} unmountOnExit>
                            <Box sx={getViewerOptionsGroupSx(zoomLevel)}>
                              {visiblePreviewFeatureMenuItems.map(({ Icon, key, label }) => (
                                <MenuItem
                                  component="div"
                                  key={key}
                                  sx={getNestedMenuItemSx(zoomLevel)}
                                  onClick={() => handleTogglePreviewFeatureOption(key)}>
                                  <ListItemIcon sx={getViewerOptionsIconSx(zoomLevel)}>
                                    <Icon fontSize="small" />
                                  </ListItemIcon>
                                  <ListItemText primary={label} />
                                  <Checkbox
                                    checked={Boolean(previewFeatureOptions?.[key])}
                                    size="small"
                                    sx={{ p: 0.25, ml: 1 }}
                                  />
                                </MenuItem>
                              ))}
                            </Box>
                          </Collapse>
                        </Box>
                        <Divider />
                      </>
                    ) : null}
                    <MenuItem
                      disabled={!hasActiveSheetLabel}
                      sx={getMenuItemSx(zoomLevel)}
                      title={activeSheetLabel}
                      onClick={() =>
                        handleOpenConfirmDialog({
                          documentReferenceId,
                          sheet: activeSheetLabel,
                        })
                      }>
                      {XLSX_VIEWER_STRINGS.MENU.CLEAR_SHEET}
                    </MenuItem>
                    <MenuItem sx={getMenuItemSx(zoomLevel)} onClick={() => handleOpenConfirmDialog({ documentReferenceId })}>
                      {XLSX_VIEWER_STRINGS.MENU.CLEAR_DOCUMENT}
                    </MenuItem>
                    <MenuItem sx={getMenuItemSx(zoomLevel)} onClick={() => handleOpenConfirmDialog()}>{XLSX_VIEWER_STRINGS.MENU.CLEAR_ALL}</MenuItem>
                  </MenuList>
                </Box>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>

      <Dialog open={confirmDialogOpen} onClose={handleCloseConfirmDialog}>
        <DialogTitle>{XLSX_VIEWER_STRINGS.DIALOG.TITLE}</DialogTitle>
        <DialogContent>
          <DialogContentText>{XLSX_VIEWER_STRINGS.DIALOG.CONTENT}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmDialog} color="primary">
            {XLSX_VIEWER_STRINGS.DIALOG.CANCEL}
          </Button>
          <Button onClick={handleConfirmClearMappings} color="error" variant="contained">
            {XLSX_VIEWER_STRINGS.DIALOG.CONFIRM}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MappingActionsToolbar;
